import React, { useRef, useState } from 'react';
import { Check, ChevronDown, Download, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../../lib/utils';
import { todayYmd } from '../../../../lib/dateRange';
import { fetchLeadsDashboardExport } from '../../../../services/leads';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { CARD_SHADOW } from '../teams/premium';
import { exportRowsToCsv } from './chartTheme';

// The Funnel's own filters (Nic 2026-10-25): the dates live in the page
// top bar (Year | Quarter | Month | Custom, shared with the team tabs), so
// this row is just the source, the per-event picker, and the lead export.

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'envoy', label: 'Envoy' },
  { value: 'referral-program', label: 'Referral Program' },
  { value: 'partners', label: 'Partners' },
  { value: 'events', label: 'Events' },
  { value: 'website', label: 'Website' },
  { value: 'manual', label: 'Manual / Other' },
  // Not a lead source (Nic 2026-10-26): direct signups, kept apart.
  { value: 'direct', label: 'Direct Signups' },
];

// The Funnel's menus (Nic 2026-10-25): on the shared Radix Popover like the
// date pickers, so everything that pops up on Lead Metrics works one way:
// attached and on screen through any resize, flips above when there's no
// room, Escape and click-outside close it, focus goes in and comes back.
// (The app-wide PickerMenu is left as it is; moving it waits for after
// launch, maybe never in v1.) Arrow keys move through the choices; the
// picked one carries a check. The list scrolls inside the rounded card
// when it runs long (the events list).
const Dropdown = ({ label, value, options, onPick, width }) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const current = options.find((o) => o.value === value) || options[0];
  const move = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(listRef.current?.querySelectorAll('[role="option"]') || [])];
    const at = items.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? Math.min(items.length - 1, at + 1) : Math.max(0, at - 1);
    items[next]?.focus();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          aria-haspopup="listbox"
          className={cn(
            'flex h-8 items-center justify-between gap-1.5 rounded-xl bg-white px-3 text-xs font-semibold text-slate-700 outline-none hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-surface-card dark:text-slate-200',
            CARD_SHADOW,
            width,
          )}
        >
          <span className="truncate">{current?.label}</span>
          <ChevronDown size={12} className={cn('shrink-0 opacity-60 transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={label}
        className="max-h-[min(340px,var(--radix-popover-content-available-height))] min-w-[max(var(--radix-popover-trigger-width),200px)] p-1 [scrollbar-color:rgba(148,163,184,0.55)_transparent] [scrollbar-width:thin]"
        // Focus lands on the picked choice, not the first one.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const picked = listRef.current?.querySelector('[aria-selected="true"]');
          (picked || listRef.current?.querySelector('[role="option"]'))?.focus();
        }}
      >
        <div ref={listRef} role="listbox" aria-label={label} onKeyDown={move}>
          {options.map((o) => {
            const on = o.value === value;
            return (
              <button
                key={String(o.value)}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => {
                  setOpen(false);
                  if (!on) onPick(o.value);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium outline-none hover:bg-slate-50 focus-visible:bg-slate-100 dark:hover:bg-white/10 dark:focus-visible:bg-white/10',
                  on ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300',
                )}
              >
                <Check size={13} strokeWidth={2.5} className={cn('shrink-0', on ? '' : 'invisible')} />
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Picking Events (or one event) jumps the dates to that event's run
// (`onWindow`); leaving for another source snaps back to the period you
// were on (`onWindow(null)`), since an event's dates mislead elsewhere.
// Also the Sales Team and Onboarding Team tabs' Source row (2026-10-25),
// without the lead export (`showExport={false}`).
const FunnelFilters = ({
  start,
  end,
  source,
  partnerId,
  filterOptions,
  onFilter,
  onWindow,
  jumped,
  showExport = true,
}) => {
  const [exporting, setExporting] = useState(false);

  const events = filterOptions?.events || [];
  const partnerIsEvent = events.some((e) => e.partnerId === partnerId);
  // A partnerId only ever arrives from the event cascade, so a non-event
  // partnerId reads as All.
  const primaryValue = partnerId ? (partnerIsEvent ? 'events' : '') : source || '';

  // A dated event's window = its saved run dates; an open end runs to today.
  const eventWindow = (ev) => (ev?.startsAt ? { start: ev.startsAt, end: ev.endsAt || todayYmd() } : null);
  // "All events": earliest start to latest end across every event.
  const allEventsWindow = () => {
    const dated = events.filter((e) => e.startsAt).map(eventWindow);
    if (!dated.length) return null;
    return { start: dated.map((w) => w.start).sort()[0], end: dated.map((w) => w.end).sort().pop() };
  };

  const emitFilter = (nextSource, nextPartnerId) => {
    if (jumped) onWindow(null);
    onFilter({ source: nextSource, partnerId: nextPartnerId });
  };

  const handleEventPick = (nextPartnerId) => {
    const win = nextPartnerId ? eventWindow(events.find((e) => e.partnerId === nextPartnerId)) : allEventsWindow();
    if (win) {
      onWindow(win);
      onFilter({ source: 'events', partnerId: nextPartnerId });
      return;
    }
    emitFilter('events', nextPartnerId);
  };

  const handlePrimary = (value) => (value === 'events' ? handleEventPick('') : emitFilter(value, ''));

  const handleRawExport = async () => {
    setExporting(true);
    try {
      const data = await fetchLeadsDashboardExport({
        start,
        end,
        ...(source ? { source } : {}),
        ...(partnerId ? { partnerId } : {}),
      });
      if (!data.rows?.length) {
        toast.info('No leads in this range to export.');
        return;
      }
      exportRowsToCsv(`leads-detail_${start}_${end}`, data.rows);
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Dropdown label="Source" value={primaryValue} options={SOURCE_OPTIONS} onPick={handlePrimary} width="w-40" />
      {primaryValue === 'events' ? (
        <Dropdown
          label="Event"
          value={partnerIsEvent ? partnerId : ''}
          options={[{ value: '', label: 'All events' }, ...events.map((ev) => ({ value: ev.partnerId, label: ev.name }))]}
          onPick={handleEventPick}
          width="w-52"
        />
      ) : null}
      {showExport ? (
      <button
        type="button"
        onClick={handleRawExport}
        disabled={exporting}
        title="Export every lead in this range with its full journey (CSV)"
        className={cn(
          'ml-auto inline-flex h-8 items-center gap-1.5 rounded-xl bg-white px-3 text-xs font-semibold text-slate-600 transition-[color] hover:text-slate-900 disabled:pointer-events-none disabled:opacity-50 dark:!bg-surface-card dark:text-slate-200',
          CARD_SHADOW,
        )}
      >
        {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        Export leads
      </button>
      ) : null}
    </div>
  );
};

export default FunnelFilters;
